/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Settings tab that lets an organisation administrator bring their own e-mail
 * sender (own SMTP server or the platform e-mail service with a verified sender
 * domain). Mirrors the EDC configuration tab pattern: toggle → form → save / test /
 * cancel, with a confirmation modal on disable. Notifications only use the custom
 * sender once the configuration is verified; until then the platform default sender
 * is used.
 */

import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {ModalService, ToastService} from '@siemens/ix-angular';
import {firstValueFrom, Subscription} from 'rxjs';

import {environment} from '../../../environments/environment';
import {
  EmailProvider,
  EmailSenderConfig,
  EmailSenderConfigRequest,
  SMTP_PORTS,
  SMTP_PORT_SUBMISSION,
  SesIdentityType,
  SmtpSecurity,
} from '../../../models/EmailSenderConfig';
import {EmailSenderConfigService} from '../../../services/email-sender-config.service';
import {UserService} from '../../../services/user.service';

const I18N = 'organizations.settings.email_sender';
/** Only domain verification is offered: it keeps the customer's DMARC policy intact. */
const SES_IDENTITY_TYPE: SesIdentityType = 'domain';
const ALL_PROVIDERS: EmailProvider[] = ['smtp', 'ses'];
const MAX_FROM_NAME_LENGTH = 100;
const MAX_HOST_LENGTH = 253;
const HOST_LABEL_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;
const IPV4_PATTERN = /^(?:\d{1,3}\.){3}\d{1,3}$/;
/** Validation error keys in display priority, mapped to their i18n message key. */
const ERROR_MESSAGE_KEYS: ReadonlyArray<[string, string]> = [
  ['required', `${I18N}.errors.required`],
  ['email', `${I18N}.errors.invalid_email`],
  ['maxlength', `${I18N}.errors.too_long`],
  ['host', `${I18N}.errors.invalid_host`],
];

type BusyFlag = 'saving' | 'testing' | 'verifying';

/** Accepts a DNS host name (TLD optional for intranet relays) or an IPv4 address. */
function isSmtpHost(value: string): boolean {
  if (!value || value.length > MAX_HOST_LENGTH) {
    return false;
  }
  if (IPV4_PATTERN.test(value)) {
    return true;
  }
  return value.split('.').every((label) => HOST_LABEL_PATTERN.test(label));
}

const smtpHostValidator: ValidatorFn = (control: AbstractControl) =>
  isSmtpHost(String(control.value ?? '')) ? null : {host: true};

/** Providers offered by this deployment (`BP_EMAIL_SENDER_PROVIDERS`), in display order. */
function offeredProviders(): EmailProvider[] {
  const configured = (environment.frontend.email_sender_providers ?? '')
    .split(',')
    .map((p) => p.trim().toLowerCase())
    .filter((p): p is EmailProvider =>
      ALL_PROVIDERS.includes(p as EmailProvider),
    );
  return configured.length > 0 ? configured : ALL_PROVIDERS;
}

interface BackendError {
  error?: {message?: string | string[]};
}

@Component({
  selector: 'app-email-sender-config',
  templateUrl: './email-sender-config.component.html',
  styleUrl: './email-sender-config.component.scss',
  standalone: false,
})
export class EmailSenderConfigComponent implements OnInit, OnDestroy {
  private readonly configService = inject(EmailSenderConfigService);
  private readonly userService = inject(UserService);
  private readonly modalService = inject(ModalService);
  private readonly translate = inject(TranslateService);
  private readonly toastService = inject(ToastService);

  @ViewChild('disableModal', {read: TemplateRef})
  disableModalRef!: TemplateRef<HTMLElement>;

  readonly securities: SmtpSecurity[] = ['starttls', 'tls', 'none'];
  readonly ports = SMTP_PORTS;
  private readonly offeredProviders = offeredProviders();

  loading = true;
  saving = false;
  testing = false;
  verifying = false;
  errorMessages: string[] = [];

  config: EmailSenderConfig | null = null;
  form!: FormGroup;

  private orgId = '';
  private userEmail = '';
  private customSenderEnabled = false;
  private userDataSubscription?: Subscription;
  private providerSubscription?: Subscription;

  get isCustomSenderEnabled(): boolean {
    return this.customSenderEnabled;
  }

  set isCustomSenderEnabled(enabled: boolean) {
    if (!enabled && this.config) {
      this.onDisableClick();
      return;
    }
    this.customSenderEnabled = enabled;
    if (enabled && !this.form) {
      this.createForm(null);
    }
  }

  get provider(): EmailProvider {
    return this.form?.get('provider')?.value as EmailProvider;
  }

  /** Offered providers plus a stored one that this deployment no longer offers. */
  get providers(): EmailProvider[] {
    const stored = this.config?.provider;
    return stored && !this.offeredProviders.includes(stored)
      ? [...this.offeredProviders, stored]
      : this.offeredProviders;
  }

  get hasProviderChoice(): boolean {
    return this.providers.length > 1;
  }

  get hasStoredPassword(): boolean {
    return Boolean(this.config?.smtp?.hasPassword);
  }

  get isVerified(): boolean {
    return this.config?.verificationStatus === 'verified';
  }

  /** The stored platform-service identity belongs to the sender domain in the form. */
  get showsStoredSesIdentity(): boolean {
    const fromAddress = String(this.form?.get('fromAddress')?.value ?? '');
    const domain = fromAddress
      .slice(fromAddress.lastIndexOf('@') + 1)
      .toLowerCase();
    return (
      this.provider === 'ses' &&
      this.config?.provider === 'ses' &&
      this.config.ses?.identity === domain
    );
  }

  /** Test mails via the platform service are only possible once the domain is verified. */
  get canSendTest(): boolean {
    return this.provider !== 'ses' || this.isVerified;
  }

  /** Cancel reverts unsaved edits, or discards a draft that was never saved. */
  get canCancel(): boolean {
    const busy = this.saving || this.testing || this.verifying;
    return !busy && (!this.config || this.form.dirty);
  }

  ngOnInit(): void {
    this.userDataSubscription = this.userService.userDataChange.subscribe(
      async (user) => {
        if (user) {
          this.orgId = user.organization?.id || '';
          this.userEmail = user.email || '';
          await this.loadConfig();
          this.userDataSubscription?.unsubscribe();
        }
      },
    );
  }

  ngOnDestroy(): void {
    this.userDataSubscription?.unsubscribe();
    this.providerSubscription?.unsubscribe();
  }

  async loadConfig(): Promise<void> {
    this.loading = true;
    this.errorMessages = [];
    try {
      this.config = await firstValueFrom(this.configService.load$(this.orgId));
    } catch {
      this.config = null;
    }
    this.customSenderEnabled = Boolean(this.config);
    this.createForm(this.config);
    this.loading = false;
  }

  createForm(config: EmailSenderConfig | null): void {
    this.form = new FormGroup({
      provider: new FormControl<EmailProvider>(
        config?.provider ?? this.offeredProviders[0],
        {nonNullable: true, validators: [Validators.required]},
      ),
      fromAddress: new FormControl(config?.fromAddress ?? '', {
        validators: [Validators.required, Validators.email],
      }),
      fromName: new FormControl(config?.fromName ?? '', {
        validators: [
          Validators.required,
          Validators.maxLength(MAX_FROM_NAME_LENGTH),
        ],
      }),
      supportEmail: new FormControl(config?.supportEmail ?? '', {
        validators: [Validators.email],
      }),
      smtp: this.createSmtpGroup(config?.smtp),
    });
    this.applyProviderValidators(this.provider);
    this.providerSubscription?.unsubscribe();
    this.providerSubscription = this.form
      .get('provider')
      ?.valueChanges.subscribe((provider: EmailProvider) =>
        this.applyProviderValidators(provider),
      );
  }

  private createSmtpGroup(smtp: EmailSenderConfig['smtp']): FormGroup {
    return new FormGroup({
      host: new FormControl(smtp?.host ?? ''),
      port: new FormControl<number>(smtp?.port ?? SMTP_PORT_SUBMISSION, {
        nonNullable: true,
      }),
      security: new FormControl<SmtpSecurity>(smtp?.security ?? 'starttls', {
        nonNullable: true,
      }),
      username: new FormControl(smtp?.username ?? ''),
      password: new FormControl(''),
    });
  }

  private applyProviderValidators(provider: EmailProvider): void {
    const smtp = this.form.get('smtp') as FormGroup;
    const smtpHost = smtp.get('host') as FormControl;
    const smtpPassword = smtp.get('password') as FormControl;

    if (provider === 'smtp') {
      smtpHost.setValidators([Validators.required, smtpHostValidator]);
      // A password is only mandatory when a username is set and nothing is stored yet.
      smtpPassword.setValidators(
        this.hasStoredPassword ? [] : [this.requiredIfUsername()],
      );
    } else {
      smtpHost.clearValidators();
      smtpPassword.clearValidators();
    }
    smtpHost.updateValueAndValidity({emitEvent: false});
    smtpPassword.updateValueAndValidity({emitEvent: false});
  }

  private requiredIfUsername(): ValidatorFn {
    return (control: AbstractControl) => {
      const username = control.parent?.get('username')?.value;
      return username && !control.value ? {required: true} : null;
    };
  }

  private toRequest(): EmailSenderConfigRequest {
    const value = this.form.getRawValue();
    const request: EmailSenderConfigRequest = {
      enabled: true,
      provider: value.provider,
      fromAddress: value.fromAddress,
      fromName: value.fromName,
      supportEmail: value.supportEmail || undefined,
    };
    if (value.provider === 'smtp') {
      request.smtp = {
        host: value.smtp.host,
        port: Number(value.smtp.port),
        security: value.smtp.security,
        username: value.smtp.username || undefined,
        password: value.smtp.password || undefined,
      };
    } else {
      request.ses = {identityType: SES_IDENTITY_TYPE};
    }
    return request;
  }

  async onSaveClick(): Promise<void> {
    this.form.markAllAsTouched();
    if (!this.form.valid) {
      return;
    }
    await this.runAction('saving', async () => {
      this.config = await firstValueFrom(
        this.configService.save$(this.orgId, this.toRequest()),
      );
      await this.notify('save_success');
      this.createForm(this.config);
    });
  }

  async onSendTestClick(): Promise<void> {
    this.form.markAllAsTouched();
    if (!this.form.valid) {
      return;
    }
    await this.runAction('testing', async () => {
      await firstValueFrom(
        this.configService.sendTest$(
          this.orgId,
          this.toRequest(),
          this.userEmail,
        ),
      );
      await this.notify('test_success', 'success', {email: this.userEmail});
      // A successful SMTP test verifies the stored configuration.
      await this.reloadConfig();
    });
  }

  async onVerifyClick(): Promise<void> {
    await this.runAction('verifying', async () => {
      this.config = await firstValueFrom(
        this.configService.verify$(this.orgId),
      );
      await (this.isVerified
        ? this.notify('verify_success')
        : this.notify('verify_pending', 'info'));
    });
  }

  /** Runs a backend action behind a busy flag and surfaces its errors in the form. */
  private async runAction(
    flag: BusyFlag,
    action: () => Promise<void>,
  ): Promise<void> {
    this[flag] = true;
    this.errorMessages = [];
    try {
      await action();
    } catch (error) {
      this.errorMessages = this.extractErrors(error);
    }
    this[flag] = false;
  }

  private notify(
    key: string,
    type: 'success' | 'info' = 'success',
    params?: Record<string, string>,
  ): Promise<unknown> {
    const message = this.translate.instant(`${I18N}.${key}`, params);
    return this.toastService.show({message, type});
  }

  private async reloadConfig(): Promise<void> {
    try {
      this.config = await firstValueFrom(this.configService.load$(this.orgId));
    } catch {
      // keep the current state; the next explicit action will surface the error
    }
  }

  onCancelClick(): void {
    this.errorMessages = [];
    this.createForm(this.config);
    if (!this.config) {
      this.customSenderEnabled = false;
    }
  }

  async onDisableClick(): Promise<void> {
    const instance = await this.modalService.open({
      content: this.disableModalRef,
      centered: true,
    });

    instance.onClose.on(async () => {
      this.loading = true;
      try {
        await firstValueFrom(this.configService.delete$(this.orgId));
        this.config = null;
        this.customSenderEnabled = false;
        this.createForm(null);
        await this.notify('delete_success');
      } catch (error) {
        this.errorMessages = this.extractErrors(error);
      }
      this.loading = false;
    });

    instance.onDismiss.on(() => {
      this.customSenderEnabled = true;
    });
  }

  getErrorMessage(path: string): string {
    const errors = this.form.get(path)?.errors;
    if (!errors) {
      return '';
    }
    const match = ERROR_MESSAGE_KEYS.find(([error]) => errors[error]);
    return match?.[1] ?? '';
  }

  isInvalid(path: string): boolean {
    const control = this.form.get(path);
    return Boolean(control && control.invalid && !control.untouched);
  }

  private extractErrors(error: unknown): string[] {
    const message = (error as BackendError)?.error?.message;
    if (Array.isArray(message)) {
      return message;
    }
    if (typeof message === 'string') {
      return [message];
    }
    return [this.translate.instant(`${I18N}.errors.generic`)];
  }
}
